(function () {
  const PICKER_SELECTOR = 'variant-picker';
  const FORM_SELECTOR = '.variant-picker__form';
  const STOCK_SELECTOR = '[data-pdp-buybox-stock]';
  const STOCK_VARIANTS_JSON_SELECTOR = '[data-stock-variants]';
  const VARIANTS_JSON_SELECTOR = '[data-variant-picker-variants]';
  const BUYBOX_PRICE_SELECTOR = '[data-pdp-buybox-price]';
  const BUYBOX_COMPARE_SELECTOR = '[data-pdp-buybox-compare]';
  const BUYBOX_DISCOUNT_SELECTOR = '[data-pdp-buybox-discount]';
  const BUYBOX_SUBROW_SELECTOR = '[data-pdp-buybox-subrow]';
  const FIELDSET_SELECTOR = 'fieldset.variant-option[data-option-position]';
  const RADIO_SELECTOR = 'input[type="radio"]';
  const LEGEND_SELECTED_SELECTOR = '.variant-option__legend-selected';
  const SIZE_OPTION_SELECTOR = '[data-is-size-option="true"]';
  const TOGGLE_SELECTOR = '[data-diameter-toggle]';
  const PICKER_MODE_STORAGE_KEY = 'diameterModes';
  const LOW_STOCK_THRESHOLD = 5;
  const MAX_ALLOWED_QUANTITY = 5;

  // ---------------------------------------------------------------------
  // Perf: cache a per-position value -> Set(variant) index for each
  // `variants` array so combination-existence checks are O(options) set
  // intersections instead of O(variants) linear scans repeated for every
  // option value in every fieldset. Keyed by array reference — the same
  // `variants` array is reused for the lifetime of a picker, so this is
  // built once per picker init, not once per sync.
  // ---------------------------------------------------------------------
  const variantIndexCache = new WeakMap();

  function buildVariantIndex(variants) {
    const byPosition = [];

    variants.forEach((variant) => {
      if (!variant || !Array.isArray(variant.options)) return;

      variant.options.forEach((value, position) => {
        const norm = normalizeValue(value);

        if (!byPosition[position]) {
          byPosition[position] = new Map();
        }

        const valueMap = byPosition[position];

        if (!valueMap.has(norm)) {
          valueMap.set(norm, new Set());
        }

        valueMap.get(norm).add(variant);
      });
    });

    return byPosition;
  }

  function getVariantIndex(variants) {
    let index = variantIndexCache.get(variants);

    if (!index) {
      index = buildVariantIndex(variants);
      variantIndexCache.set(variants, index);
    }

    return index;
  }

  function intersectSets(a, b) {
    const [small, large] = a.size <= b.size ? [a, b] : [b, a];
    const result = new Set();

    for (const value of small) {
      if (large.has(value)) {
        result.add(value);
      }
    }

    return result;
  }

  function parseJSON(el) {
    if (!el) return [];

    try {
      return JSON.parse(el.textContent || '[]');
    } catch (error) {
      console.warn('[variant-picker] json parse failed', error);
      return [];
    }
  }

  function normalizeValue(value) {
    return String(value == null ? '' : value).trim();
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || "EUR";
    const locale = "de-DE";

    const amount = (Number(cents) || 0) / 100;
    const hasDecimals = amount % 1 !== 0;

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0
      }).format(amount);
    } catch (error) {
      return hasDecimals
        ? `${amount.toFixed(2).replace(".", ",")} €`
        : `${Math.round(amount)} €`;
    }
  }

  function getDiscountLabel(price, compareAtPrice) {
    if (!compareAtPrice || compareAtPrice <= price) {
      return '';
    }

    const discountAmount = compareAtPrice - price;
    const discountPct = Math.floor((discountAmount * 100) / compareAtPrice + 0.5);

    return `${discountPct}% Rabatt`;
  }

  function getSizeScore(value) {
    const raw = normalizeValue(value).replace(',', '.');
    const numberMatch = raw.match(/(\d+(?:\.\d+)?)/);

    if (numberMatch) {
      return Number(numberMatch[1]);
    }

    return null;
  }

  function sortValuesDesc(a, b) {
    const aVal = normalizeValue(a);
    const bVal = normalizeValue(b);

    const aSize = getSizeScore(aVal);
    const bSize = getSizeScore(bVal);

    if (aSize !== null && bSize !== null) {
      return bSize - aSize;
    }

    if (aSize !== null) return -1;
    if (bSize !== null) return 1;

    return bVal.localeCompare(aVal, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }

  function getElementTarget(event) {
    const target = event.target;

    if (target instanceof Element) {
      return target;
    }

    if (target instanceof Node && target.parentElement instanceof Element) {
      return target.parentElement;
    }

    return null;
  }

  function getPickerFromNode(node) {
    if (!(node instanceof Element)) return null;
    return node.closest(PICKER_SELECTOR);
  }

  function getProductFormComponentFromPicker(picker) {
    if (!picker) return null;

    const sectionRoot = picker.closest('.shopify-section');
    if (sectionRoot) {
      return sectionRoot.querySelector('product-form-component');
    }

    const drawerContent = picker.closest('#quick-add-drawer-content');
    if (drawerContent) {
      return drawerContent.querySelector('product-form-component');
    }

    return null;
  }

  function getLegendSelected(fieldset) {
    return fieldset?.querySelector(LEGEND_SELECTED_SELECTOR) || null;
  }

  function getStockElement(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return productForm.querySelector(STOCK_SELECTOR);
  }

  function getBuyboxPriceElement(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return productForm.querySelector(BUYBOX_PRICE_SELECTOR);
  }

  function getBuyboxCompareElement(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return productForm.querySelector(BUYBOX_COMPARE_SELECTOR);
  }

  function getBuyboxDiscountElement(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return productForm.querySelector(BUYBOX_DISCOUNT_SELECTOR);
  }

  function getBuyboxSubrowElement(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return productForm.querySelector(BUYBOX_SUBROW_SELECTOR);
  }

  function getStockVariants(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return [];

    return parseJSON(productForm.querySelector(STOCK_VARIANTS_JSON_SELECTOR));
  }

  function getVariantInput(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return (
      productForm.querySelector('input[ref="variantId"]') ||
      productForm.querySelector('input[name="id"]')
    );
  }

  function getQuantityInput(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return (
      productForm.querySelector('quantity-input input[name="quantity"]') ||
      productForm.querySelector('input[name="quantity"]')
    );
  }

  function getAddToCartButton(picker) {
    const productForm = getProductFormComponentFromPicker(picker);
    if (!productForm) return null;

    return (
      productForm.querySelector('button[name="add"]') ||
      productForm.querySelector('[type="submit"][name="add"]') ||
      productForm.querySelector('.product-form__submit')
    );
  }

  function syncVariantInput(picker, variantLike) {
    if (!picker || !variantLike?.id) return;

    const input = getVariantInput(picker);
    if (!input) return;

    const nextId = String(variantLike.id);
    const prevId = String(input.value || input.getAttribute('value') || '');

    if (prevId === nextId) return;

    input.value = nextId;
    input.setAttribute('value', nextId);

    input.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );

    input.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  function getCurrentVariantId(picker) {
    const input = getVariantInput(picker);
    if (!input) return null;

    const value = input.value || input.getAttribute('value') || input.dataset.initialVariantId || '';
    return value ? String(value) : null;
  }

  function findStockVariantById(picker, variantId) {
    if (!variantId) return null;

    const variants = getStockVariants(picker);
    return variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  function findVariantById(variants, variantId) {
    if (!variantId || !Array.isArray(variants)) return null;
    return variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  function hasFullStockData(variant) {
    return Boolean(
      variant &&
        Object.prototype.hasOwnProperty.call(variant, 'available') &&
        Object.prototype.hasOwnProperty.call(variant, 'inventory_quantity') &&
        Object.prototype.hasOwnProperty.call(variant, 'inventory_management') &&
        Object.prototype.hasOwnProperty.call(variant, 'inventory_policy')
    );
  }

  function resolveStockVariant(picker, variantLike) {
    if (!variantLike) return null;

    if (hasFullStockData(variantLike)) {
      return variantLike;
    }

    if (variantLike.id != null) {
      return findStockVariantById(picker, variantLike.id);
    }

    return null;
  }

  function getInitialVariantFromPicker(picker) {
    const variantId = getCurrentVariantId(picker);
    if (variantId) {
      return findStockVariantById(picker, variantId);
    }

    const variants = getStockVariants(picker);
    return variants[0] || null;
  }

  function isSizeFieldset(fieldset) {
    return fieldset?.dataset?.isSizeOption === 'true';
  }

  function isDiameterMode(fieldset) {
    return fieldset?.classList.contains('is-diameter-mode');
  }

  function getModeStateFromPicker(picker) {
    if (!picker) return {};

    try {
      return JSON.parse(picker.dataset[PICKER_MODE_STORAGE_KEY] || '{}');
    } catch (error) {
      return {};
    }
  }

  function setModeStateToPicker(picker, state) {
    if (!picker) return;
    picker.dataset[PICKER_MODE_STORAGE_KEY] = JSON.stringify(state || {});
  }

  function saveDiameterMode(fieldset, enabled) {
    if (!fieldset) return;

    const picker = getPickerFromNode(fieldset);
    if (!picker) return;

    const optionPosition = fieldset.dataset.optionPosition;
    if (optionPosition == null) return;

    const state = getModeStateFromPicker(picker);
    state[optionPosition] = Boolean(enabled);
    setModeStateToPicker(picker, state);
  }

  function readDiameterMode(fieldset) {
    if (!fieldset) return false;

    const picker = getPickerFromNode(fieldset);
    if (!picker) return false;

    const optionPosition = fieldset.dataset.optionPosition;
    if (optionPosition == null) return false;

    const state = getModeStateFromPicker(picker);
    return state[optionPosition] === true;
  }

  function applyDiameterMode(fieldset, enabled) {
    if (!fieldset || !isSizeFieldset(fieldset)) return;

    const nextState = Boolean(enabled);
    const toggle = fieldset.querySelector(TOGGLE_SELECTOR);

    fieldset.classList.toggle('is-diameter-mode', nextState);
    fieldset.dataset.diameterMode = nextState ? 'true' : 'false';

    if (toggle) {
      toggle.setAttribute('aria-checked', String(nextState));
    }

    saveDiameterMode(fieldset, nextState);
  }

  function restoreDiameterMode(fieldset) {
    if (!fieldset || !isSizeFieldset(fieldset)) return;

    const localState = fieldset.dataset.diameterMode === 'true';
    const pickerState = readDiameterMode(fieldset);
    applyDiameterMode(fieldset, pickerState || localState);
  }

  function restoreAllDiameterModes(root) {
    if (!root) return;

    root.querySelectorAll(SIZE_OPTION_SELECTOR).forEach((fieldset) => {
      restoreDiameterMode(fieldset);
    });
  }

  function getLabelDisplayValue(label, fieldset) {
    if (!label || !fieldset) return '';

    if (isDiameterMode(fieldset)) {
      const type2 = label.querySelector('.type-2');
      const type2Value = normalizeValue(type2?.textContent);
      if (type2Value) return type2Value;
    }

    const type1 = label.querySelector('.type-1');
    const type1Value = normalizeValue(type1?.textContent);
    if (type1Value) return type1Value;

    const text = label.querySelector('.variant-option__button-label__text');
    const textValue = normalizeValue(text?.textContent);
    if (textValue) return textValue;

    const input = label.querySelector(RADIO_SELECTOR);
    return input ? normalizeValue(input.value) : '';
  }

  function getCheckedInput(fieldset) {
    return fieldset?.querySelector(`${RADIO_SELECTOR}:checked`) || null;
  }

  function getCheckedDisplayValue(fieldset) {
    const checked = getCheckedInput(fieldset);
    if (!checked) return '';

    const label = checked.closest('.variant-option__button-label');
    return label ? getLabelDisplayValue(label, fieldset) : normalizeValue(checked.value);
  }

  function setLegendValue(fieldset, value, hover) {
    const legend = getLegendSelected(fieldset);
    if (!legend) return;

    legend.textContent = value || '';
    legend.style.color = hover ? 'var(--Blues-Primary-Blue, #1E35B1)' : '';
  }

  function refreshFieldsetLegend(fieldset) {
    if (!fieldset) return;
    setLegendValue(fieldset, getCheckedDisplayValue(fieldset), false);
  }

  function refreshAllLegends(root) {
    if (!root) return;

    root.querySelectorAll(FIELDSET_SELECTOR).forEach((fieldset) => {
      refreshFieldsetLegend(fieldset);
    });
  }

  function getSelectedOptions(picker) {
    const form = picker.querySelector(FORM_SELECTOR);
    if (!form) return [];

    const selected = [];

    form.querySelectorAll(FIELDSET_SELECTOR).forEach((fieldset) => {
      const position = Number(fieldset.dataset.optionPosition);
      const checked = getCheckedInput(fieldset);
      selected[position] = checked ? normalizeValue(checked.value) : '';
    });

    return selected;
  }

  // ---------------------------------------------------------------------
  // Perf: replaces the original O(variants) `.some()` scan with an
  // indexed set-intersection lookup. Same semantics: does a variant exist
  // whose value at `optionPosition` is `optionValue`, and whose values at
  // every *other already-selected* position match `selectedOptions`.
  // ---------------------------------------------------------------------
  function variantCombinationExists(variants, selectedOptions, optionPosition, optionValue) {
    const index = getVariantIndex(variants);
    const candidateValue = normalizeValue(optionValue);

    let resultSet = index[optionPosition]?.get(candidateValue);
    if (!resultSet || resultSet.size === 0) return false;

    for (let position = 0; position < selectedOptions.length; position++) {
      if (position === optionPosition) continue;

      const selectedValue = normalizeValue(selectedOptions[position]);
      if (!selectedValue) continue;

      const positionSet = index[position]?.get(selectedValue);
      if (!positionSet || positionSet.size === 0) return false;

      resultSet = intersectSets(resultSet, positionSet);
      if (resultSet.size === 0) return false;
    }

    return resultSet.size > 0;
  }

  function sortSizeFieldset(fieldset) {
    if (!fieldset || !isSizeFieldset(fieldset)) return;

    const labels = Array.from(fieldset.children).filter((node) => {
      return (
        node instanceof HTMLElement &&
        node.classList.contains('variant-option__button-label')
      );
    });

    if (!labels.length) return;

    labels.sort((a, b) => {
      return sortValuesDesc(
        a.dataset.optionValue || a.textContent || '',
        b.dataset.optionValue || b.textContent || ''
      );
    });

    // Perf: one DOM mutation instead of N. appendChild-ing each label
    // individually (the previous approach) forces N sequential reflows on
    // a fieldset with many options (e.g. a full ring-size range) - moving
    // an already-attached node still dirties layout each time, even though
    // the on-screen result is identical either way. Building the new order
    // in a detached DocumentFragment first and inserting it once produces
    // the exact same final DOM order in a single mutation.
    const fragment = document.createDocumentFragment();
    labels.forEach((label) => {
      fragment.appendChild(label);
    });
    fieldset.appendChild(fragment);
  }

  function sortAllSizeFieldsets(picker) {
    if (!picker) return;

    picker
      .querySelectorAll(`${FIELDSET_SELECTOR}${SIZE_OPTION_SELECTOR}`)
      .forEach((fieldset) => {
        sortSizeFieldset(fieldset);
      });
  }

  function buildFieldsetState(fieldset, variants, selectedOptions) {
    const optionPosition = Number(fieldset.dataset.optionPosition);
    const labels = Array.from(fieldset.querySelectorAll('.variant-option__button-label'));

    const items = labels
      .map((label) => {
        const input = label.querySelector(RADIO_SELECTOR);
        if (!input) return null;

        const optionValue = normalizeValue(input.value);
        const existsForSelection = variantCombinationExists(
          variants,
          selectedOptions,
          optionPosition,
          optionValue
        );

        return {
          label,
          input,
          value: optionValue,
          exists: existsForSelection,
          checked: input.checked
        };
      })
      .filter(Boolean);

    let nextCheckedValue = null;

    const checkedVisible = items.find((item) => item.exists && item.checked);
    if (checkedVisible) {
      nextCheckedValue = checkedVisible.value;
    } else {
      const firstVisible = items.find((item) => item.exists);
      if (firstVisible) {
        nextCheckedValue = firstVisible.value;
      }
    }

    return {
      fieldset,
      items,
      visibleCount: items.filter((item) => item.exists).length,
      nextCheckedValue
    };
  }

  function applyFieldsetState(state) {
    if (!state || !state.fieldset) return false;

    let changed = false;

    state.items.forEach((item) => {
      const shouldShow = item.exists;
      const shouldCheck = item.value === state.nextCheckedValue;

      if (item.label.hidden !== !shouldShow) {
        item.label.hidden = !shouldShow;
        changed = true;
      }

      const nextDisplay = shouldShow ? '' : 'none';
      if (item.label.style.display !== nextDisplay) {
        item.label.style.display = nextDisplay;
        changed = true;
      }

      const nextDisabled = !shouldShow;
      if (item.input.disabled !== nextDisabled) {
        item.input.disabled = nextDisabled;
        changed = true;
      }

      const nextAriaHidden = String(!shouldShow);
      if (item.input.getAttribute('aria-hidden') !== nextAriaHidden) {
        item.input.setAttribute('aria-hidden', nextAriaHidden);
        changed = true;
      }

      if (item.input.checked !== shouldCheck) {
        item.input.checked = shouldCheck;
        changed = true;
      }
    });

    const shouldHideFieldset = state.visibleCount === 0;

    if (state.fieldset.hidden !== shouldHideFieldset) {
      state.fieldset.hidden = shouldHideFieldset;
      changed = true;
    }

    const nextFieldsetDisplay = shouldHideFieldset ? 'none' : '';
    if (state.fieldset.style.display !== nextFieldsetDisplay) {
      state.fieldset.style.display = nextFieldsetDisplay;
      changed = true;
    }

    refreshFieldsetLegend(state.fieldset);

    return changed;
  }

  function findCurrentVariant(variants, selectedOptions) {
    return (
      variants.find((variant) => {
        if (!variant || !Array.isArray(variant.options)) return false;

        return variant.options.every((value, index) => {
          return normalizeValue(value) === normalizeValue(selectedOptions[index]);
        });
      }) || null
    );
  }

  function getStockData(variant) {
    if (!variant) {
      return {
        state: 'out',
        text: 'Ausverkauft'
      };
    }

    const inventoryManaged = variant.inventory_management === 'shopify';
    const inventoryQuantity = Number(variant.inventory_quantity || 0);
    const inventoryPolicy = normalizeValue(variant.inventory_policy);
    const quantityRuleMin = Number(variant.quantity_rule?.min || 1);

    let canAddToCart = false;

    if (inventoryManaged) {
      const quantityRuleSoldout =
        inventoryPolicy === 'deny' && quantityRuleMin > inventoryQuantity;

      if ((inventoryQuantity <= 0 && inventoryPolicy === 'deny') || quantityRuleSoldout) {
        canAddToCart = false;
      } else {
        canAddToCart = true;
      }
    } else {
      canAddToCart = Boolean(variant.available);
    }

    if (!canAddToCart) {
      return {
        state: 'out',
        text: 'Ausverkauft'
      };
    }

    if (
      inventoryManaged &&
      inventoryPolicy === 'deny' &&
      inventoryQuantity > 0 &&
      inventoryQuantity < LOW_STOCK_THRESHOLD
    ) {
      return {
        state: 'low',
        text: `Begrenzter Bestand: ${inventoryQuantity} Stück übrig`
      };
    }

    return {
      state: 'available',
      text: 'Auf Lager'
    };
  }

  function getQuantityLimit(variant) {
    if (!variant) return 0;

    const inventoryManaged = variant.inventory_management === 'shopify';
    const inventoryQuantity = Number(variant.inventory_quantity || 0);
    const inventoryPolicy = normalizeValue(variant.inventory_policy);
    const quantityRuleMin = Number(variant.quantity_rule?.min || 1);

    if (inventoryManaged) {
      const quantityRuleSoldout =
        inventoryPolicy === 'deny' && quantityRuleMin > inventoryQuantity;

      if ((inventoryQuantity <= 0 && inventoryPolicy === 'deny') || quantityRuleSoldout) {
        return 0;
      }

      if (inventoryPolicy === 'deny') {
        return Math.min(MAX_ALLOWED_QUANTITY, inventoryQuantity);
      }

      return MAX_ALLOWED_QUANTITY;
    }

    return variant.available ? MAX_ALLOWED_QUANTITY : 0;
  }

  function updateQuantityState(picker, variantLike) {
    if (!picker) return;

    const quantityInput = getQuantityInput(picker);
    if (!quantityInput) return;

    const variant = resolveStockVariant(picker, variantLike);
    const limit = getQuantityLimit(variant);
    const min = Math.max(1, Number(variant?.quantity_rule?.min || 1));

    quantityInput.min = String(min);

    if (limit > 0) {
      quantityInput.max = String(limit);
      quantityInput.disabled = false;
      quantityInput.removeAttribute('aria-disabled');

      const currentValue = Number(quantityInput.value || min);
      let nextValue = currentValue;

      if (currentValue > limit) {
        nextValue = limit;
      } else if (currentValue < min) {
        nextValue = min;
      }

      if (String(quantityInput.value) !== String(nextValue)) {
        quantityInput.value = String(nextValue);
        quantityInput.setAttribute('value', String(nextValue));
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      quantityInput.max = '0';
      quantityInput.value = String(min);
      quantityInput.setAttribute('value', String(min));
      quantityInput.disabled = true;
      quantityInput.setAttribute('aria-disabled', 'true');
    }
  }

  function updateAddToCartState(picker, variantLike) {
    if (!picker) return;

    const button = getAddToCartButton(picker);
    if (!button) return;

    const variant = resolveStockVariant(picker, variantLike);
    const quantityLimit = getQuantityLimit(variant);
    const available = variant == null ? false : Boolean(variant.available);
    const canAdd = available && quantityLimit > 0;

    button.disabled = !canAdd;
    button.setAttribute('aria-disabled', String(!canAdd));
  }

  function updateBuyboxPrice(picker, variantLike, variants) {
    if (!picker) return;

    const priceEl = getBuyboxPriceElement(picker);
    const compareEl = getBuyboxCompareElement(picker);
    const discountEl = getBuyboxDiscountElement(picker);
    const subrowEl = getBuyboxSubrowElement(picker);

    if (!priceEl) return;

    let variant = variantLike || null;

    if ((!variant || variant.price == null) && variant?.id != null) {
      variant = findVariantById(variants, variant.id);
    }

    if (!variant) {
      const currentVariantId = getCurrentVariantId(picker);
      variant = findVariantById(variants, currentVariantId);
    }

    if (!variant) return;

    const price = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const hasCompare = compareAtPrice > price;

    priceEl.textContent = formatMoney(price);

    if (compareEl) {
      compareEl.textContent = hasCompare ? formatMoney(compareAtPrice) : '';
    }

    if (discountEl) {
      discountEl.textContent = hasCompare
        ? getDiscountLabel(price, compareAtPrice)
        : '';
    }

    if (subrowEl) {
      subrowEl.hidden = !hasCompare;
    }
  }

  function renderStockState(stockEl, stockData) {
    stockEl.textContent = stockData.text;
    stockEl.dataset.stockState = stockData.state;
    stockEl.classList.remove(
      'pdp-buybox__stock--available',
      'pdp-buybox__stock--low',
      'pdp-buybox__stock--out'
    );
    stockEl.classList.add(`pdp-buybox__stock--${stockData.state}`);
  }

  function updateLegendLowStockNote(picker, variantLike) {
    if (!picker?.closest('#quick-add-drawer-content')) return;

    const note = picker.querySelector('[data-variant-legend-low-stock]');
    if (!note) return;

    const stockVariant = resolveStockVariant(picker, variantLike);
    if (!stockVariant) {
      note.textContent = '';
      note.hidden = true;
      delete note.dataset.stockLegendState;
      return;
    }

    const stockData = getStockData(stockVariant);
    note.textContent = ` (${stockData.text})`;
    note.hidden = false;
    note.dataset.stockLegendState = stockData.state;
  }

  function updateStockMessageFromVariant(picker, variantLike) {
    if (!picker) return;

    const stockVariant = resolveStockVariant(picker, variantLike);
    const stockData = getStockData(stockVariant);
    const stockEl = getStockElement(picker);

    if (stockEl) {
      renderStockState(stockEl, stockData);
    }

    updateLegendLowStockNote(picker, variantLike);
  }

  function updateStateFromCurrentInput(picker, variants) {
    if (!picker) return;

    const variantId = getCurrentVariantId(picker);
    const stockVariant = findStockVariantById(picker, variantId);
    const currentVariant = findVariantById(variants, variantId);

    updateStockMessageFromVariant(picker, stockVariant);
    updateQuantityState(picker, stockVariant);
    updateAddToCartState(picker, stockVariant);
    updateBuyboxPrice(picker, currentVariant, variants);
  }

  // ---------------------------------------------------------------------
  // Perf: query the fieldsets once and reuse across guard-loop iterations
  // instead of re-running querySelectorAll on every pass.
  // ---------------------------------------------------------------------
  function enforceValidState(picker, variants) {
    let guard = 0;
    let changed = false;

    const fieldsetEls = Array.from(picker.querySelectorAll(FIELDSET_SELECTOR));

    do {
      changed = false;
      const selectedOptions = getSelectedOptions(picker);

      const states = fieldsetEls.map((fieldset) => {
        return buildFieldsetState(fieldset, variants, selectedOptions);
      });

      states.forEach((state) => {
        const didChange = applyFieldsetState(state);
        if (didChange) changed = true;
      });

      guard += 1;
    } while (changed && guard < 5);

    const finalSelectedOptions = getSelectedOptions(picker);
    const currentVariant = findCurrentVariant(variants, finalSelectedOptions);

    if (currentVariant) {
      syncVariantInput(picker, currentVariant);

      picker.dispatchEvent(
        new CustomEvent('variant:sync', {
          bubbles: true,
          detail: {
            resource: currentVariant
          }
        })
      );
    }
  }

  // ---------------------------------------------------------------------
  // Perf: the second (rAF) pass is a safety net for cases where the
  // synchronous pass didn't land on a valid variant (e.g. DOM not fully
  // settled yet). If the sync pass already resolved a variant, we skip
  // re-running the full pipeline a second time.
  // ---------------------------------------------------------------------
  function syncPicker(picker, variants) {
    if (!picker || !variants?.length) return;

    enforceValidState(picker, variants);
    sortAllSizeFieldsets(picker);
    restoreAllDiameterModes(picker);
    refreshAllLegends(picker);

    const selectedOptions = getSelectedOptions(picker);
    const currentVariant = findCurrentVariant(variants, selectedOptions);

    if (currentVariant) {
      syncVariantInput(picker, currentVariant);
      updateStockMessageFromVariant(picker, currentVariant);
      updateQuantityState(picker, currentVariant);
      updateAddToCartState(picker, currentVariant);
      updateBuyboxPrice(picker, currentVariant, variants);
      return;
    }

    window.requestAnimationFrame(() => {
      sortAllSizeFieldsets(picker);
      restoreAllDiameterModes(picker);
      refreshAllLegends(picker);

      const latestSelectedOptions = getSelectedOptions(picker);
      const latestVariant = findCurrentVariant(variants, latestSelectedOptions);

      if (latestVariant) {
        syncVariantInput(picker, latestVariant);
        updateStockMessageFromVariant(picker, latestVariant);
        updateQuantityState(picker, latestVariant);
        updateAddToCartState(picker, latestVariant);
        updateBuyboxPrice(picker, latestVariant, variants);
      } else {
        updateStateFromCurrentInput(picker, variants);
      }
    });
  }

  function initVariantPicker(picker) {
    if (!picker || picker.dataset.availabilityInitialized === 'true') return;

    const variants = parseJSON(picker.querySelector(VARIANTS_JSON_SELECTOR));
    if (!variants.length) {
      console.warn('[variant-picker] no variants found');
      return;
    }

    let syncing = false;

    const sync = function () {
      if (syncing) return;
      syncing = true;

      syncPicker(picker, variants);

      window.requestAnimationFrame(() => {
        syncing = false;
      });
    };

    sync();

    picker.addEventListener('variant:update', function () {
      sync();
    });

    picker.addEventListener('change', function (event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== 'radio') return;

      sync();
    });

    const variantInput = getVariantInput(picker);
    if (variantInput && !variantInput.dataset.stockObserverAttached) {
      const observer = new MutationObserver(() => {
        updateStateFromCurrentInput(picker, variants);
      });

      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      variantInput.addEventListener('change', function () {
        updateStateFromCurrentInput(picker, variants);
      });

      variantInput.dataset.stockObserverAttached = 'true';
    }

    picker.dataset.availabilityInitialized = 'true';
  }

  // ---------------------------------------------------------------------
  // Perf: on pages with many pickers (e.g. a collection grid with a
  // quick-add drawer per product card), initializing all of them
  // synchronously in one forEach is exactly the shape of a long blocking
  // task. Spread the work across idle time / frames instead so no single
  // task monopolizes the main thread.
  // ---------------------------------------------------------------------
  function initAllVariantPickers() {
    const pickers = Array.from(document.querySelectorAll(PICKER_SELECTOR));
    if (!pickers.length) return;

    const schedule =
      window.requestIdleCallback ||
      function (cb) {
        return window.requestAnimationFrame(() => cb({ timeRemaining: () => 1 }));
      };

    let i = 0;

    function step(deadline) {
      while (i < pickers.length && (deadline?.timeRemaining ? deadline.timeRemaining() > 0 : true)) {
        initVariantPicker(pickers[i]);
        i += 1;
      }

      if (i < pickers.length) {
        schedule(step);
      }
    }

    schedule(step);
  }

  document.addEventListener(
    'click',
    function (event) {
      const target = getElementTarget(event);
      if (!target) return;

      const toggle = target.closest(TOGGLE_SELECTOR);
      if (toggle) {
        const fieldset = toggle.closest(SIZE_OPTION_SELECTOR);
        if (!fieldset) return;

        const nextState = toggle.getAttribute('aria-checked') !== 'true';
        applyDiameterMode(fieldset, nextState);
        refreshFieldsetLegend(fieldset);
        return;
      }

      const label = target.closest('.variant-option__button-label');
      if (!label) return;

      const fieldset = label.closest('.variant-option');
      if (!fieldset) return;

      setLegendValue(fieldset, getLabelDisplayValue(label, fieldset), false);
    },
    false
  );

  document.addEventListener(
    'keydown',
    function (event) {
      const toggle = event.target.closest(TOGGLE_SELECTOR);
      if (!toggle) return;

      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();

      const fieldset = toggle.closest(SIZE_OPTION_SELECTOR);
      if (!fieldset) return;

      const nextState = toggle.getAttribute('aria-checked') !== 'true';
      applyDiameterMode(fieldset, nextState);
      refreshFieldsetLegend(fieldset);
    },
    false
  );

  document.addEventListener(
    'mouseenter',
    function (event) {
      const target = getElementTarget(event);
      if (!target) return;

      const label = target.closest('.variant-option__button-label');
      if (!label) return;

      const fieldset = label.closest('.variant-option');
      if (!fieldset) return;

      setLegendValue(fieldset, getLabelDisplayValue(label, fieldset), true);
    },
    true
  );

  document.addEventListener(
    'mouseleave',
    function (event) {
      const target = getElementTarget(event);
      if (!target) return;

      const label = target.closest('.variant-option__button-label');
      if (!label) return;

      const fieldset = label.closest('.variant-option');
      if (!fieldset) return;

      refreshFieldsetLegend(fieldset);
    },
    true
  );

  document.addEventListener(
    'variant:update',
    function (event) {
      const picker = getPickerFromNode(event.target);
      if (!picker) return;

      restoreAllDiameterModes(picker);
      refreshAllLegends(picker);
    },
    true
  );

  document.addEventListener(
    'variant:sync',
    function (event) {
      const picker = getPickerFromNode(event.target);
      if (!picker) return;

      const currentVariant = event.detail?.resource || null;

      restoreAllDiameterModes(picker);
      refreshAllLegends(picker);

      if (currentVariant?.id) {
        syncVariantInput(picker, currentVariant);
      }

      window.requestAnimationFrame(() => {
        const stockVariant = resolveStockVariant(picker, currentVariant);
        const variants = parseJSON(picker.querySelector(VARIANTS_JSON_SELECTOR));

        updateStockMessageFromVariant(picker, stockVariant);
        updateQuantityState(picker, stockVariant);
        updateAddToCartState(picker, stockVariant);
        updateBuyboxPrice(picker, currentVariant, variants);
      });
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllVariantPickers);
  } else {
    initAllVariantPickers();
  }

  document.addEventListener('shopify:section:load', initAllVariantPickers);

  document.addEventListener('quick-add-drawer:content-updated', (event) => {
    const container = event.detail?.container;
    if (!(container instanceof HTMLElement)) return;

    container.querySelectorAll(PICKER_SELECTOR).forEach((picker) => {
      if (!(picker instanceof HTMLElement)) return;
      delete picker.dataset.availabilityInitialized;
      initVariantPicker(picker);
    });
  });
})();